
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Phone, Smartphone } from "lucide-react";

interface RegisteredPhone {
  id: string;
  phone_number: string;
  device_id: string;
  is_online: boolean;
  last_heartbeat: string | null;
}

export const SmsSettings = () => {
  const { toast } = useToast();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [registeredPhones, setRegisteredPhones] = useState<RegisteredPhone[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRegisteredPhones();
  }, []);

  const loadRegisteredPhones = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: phones, error } = await supabase
        .from('registered_phones')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setRegisteredPhones(phones || []);
    } catch (error: any) {
      console.error('Error loading phones:', error);
      toast({
        title: "Error loading registered phones",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const registerPhone = async () => {
    if (!phoneNumber || !deviceId) {
      toast({
        title: "Missing information",
        description: "Please provide both phone number and device ID",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from('registered_phones')
        .insert({
          user_id: user.id,
          phone_number: phoneNumber,
          device_id: deviceId,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Phone registered successfully",
      });

      setPhoneNumber("");
      setDeviceId("");
      loadRegisteredPhones();
    } catch (error: any) {
      console.error('Error registering phone:', error);
      toast({
        title: "Error registering phone",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="w-5 h-5" />
          SMS Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Phone Number</Label>
            <Input
              id="phoneNumber"
              placeholder="+1234567890"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="deviceId">Device ID</Label>
            <Input
              id="deviceId"
              placeholder="Enter device ID"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
            />
          </div>

          <Button 
            onClick={registerPhone} 
            disabled={loading}
            className="w-full"
          >
            Register New Phone
          </Button>
        </div>

        {registeredPhones.length > 0 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Registered Phones</h3>
            <div className="space-y-2">
              {registeredPhones.map((phone) => (
                <div
                  key={phone.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-pink-50"
                >
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-pink-500" />
                    <span>{phone.phone_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${phone.is_online ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <span className="text-sm text-gray-600">
                      {phone.is_online ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
